import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { PageTitle } from "@/components/layout/PageTitle";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { useIsTablet } from "@/hooks/use-mobile";
import { PageHeader } from "@/components/layout/PageHeaderSlot";
import { Button } from "@/components/ui/button";
import { ConfigurationPanel } from "@/components/configuration";

/**
 * "Probably actually like a configuration tab that lets you set up your storage
 * containers, like all that stuff under a config tab" (T-1006, transcript 53:50).
 * A plain toggle rather than Radix's tabs: this is two panels, and a button a
 * keyboard already understands is enough.
 */
type SettingsTab = "account" | "configuration";

export const Route = createFileRoute("/(protected)/_layout/settings")({
  component: SettingsComponent,
});

function SettingsComponent() {
  const { user, isAuthenticated, isLoading, hasPermission } = useAuth();
  const isTablet = useIsTablet();
  const [tab, setTab] = useState<SettingsTab>("account");
  const isLoadingData = isLoading || !isAuthenticated;

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-(--color-text-secondary)">{i18n._("Loading...")}</div>
      </div>
    );
  }

  // The Account panel is self-service — it shows the signed-in user their own
  // name, email and concept. Gating the whole route on `user:read` locked every
  // role without user-management rights (a chef, say) out of their own account
  // page. The permission belongs on Configuration, which is org-scoped.
  const canConfigure = hasPermission("user:read");
  const activeTab = tab === "configuration" && !canConfigure ? "account" : tab;

  return (
    <div className="space-y-6">
      {isTablet ? (
        <PageHeader>
          <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
            <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary)">
              {i18n._("Settings")}
            </h1>
          </div>
        </PageHeader>
      ) : (
        <PageTitle
          title={i18n._("Settings")}
          description={i18n._("Manage your account settings and preferences")}
        />
      )}

      <div className="flex h-10 w-80 items-center rounded-lg border border-(--color-border) bg-(--color-surface-muted)/50 p-1">
        {(canConfigure
          ? (["account", "configuration"] as SettingsTab[])
          : (["account"] as SettingsTab[])
        ).map((name) => (
          <Button
            key={name}
            variant="ghost"
            onClick={() => setTab(name)}
            className={`h-full flex-1 text-sm font-medium ${
              activeTab === name
                ? "bg-(--color-surface-page) shadow-xs text-(--color-text-primary) hover:bg-(--color-surface-page)"
                : "text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
            }`}
          >
            {name === "account" ? i18n._("Account") : i18n._("Configuration")}
          </Button>
        ))}
      </div>

      {activeTab === "configuration" ? (
        <ConfigurationPanel canManage={hasPermission("organization:update")} />
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>
            <Trans>Account Information</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Your account details</Trans>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">
              <Trans>Name</Trans>
            </label>
            <p className="text-sm text-(--color-text-secondary)">
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium">
              <Trans>Email</Trans>
            </label>
            <p className="text-sm text-(--color-text-secondary)">{user?.email}</p>
          </div>
          {user?.company && (
            <div>
              <label className="text-sm font-medium">
                <Trans>Concept</Trans>
              </label>
              <p className="text-sm text-(--color-text-secondary)">
                {user.company.name}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
